import * as React from "react";
import { Link } from "react-router-dom";
import { TcpaConsentText } from "./TcpaConsentText.js";
import "../styles/marketing-consent.css";

/**
 * The single reusable SMS / TCPA marketing opt-in control shared by every
 * account-creation surface — the homepage auth-gate popup and the standalone
 * `/signup` page. Renders the checkbox chrome + an always-visible summary
 * that carries EVERY material term required for prior express written
 * consent under 47 CFR §64.1200(f)(9): express consent to marketing
 * texts/calls/voicemails, the brand, autodialer/prerecorded-voice
 * authorization, consent-not-a-condition-of-purchase, msg/data rates,
 * reply-STOP, and the Terms/Privacy links. Only the formal long-form
 * paragraph (which restates the same terms plus the Do-Not-Call clause)
 * collapses behind the "Read full SMS consent" expander.
 *
 * COMPLIANCE INVARIANT: no material term may live ONLY inside the collapsed
 * text. TCPA consent must stay clear and conspicuous at the point of consent,
 * and A2P 10DLC carrier reviewers screenshot this form — a disclosure hidden
 * behind a toggle is a common campaign-rejection reason.
 *
 * Per-surface look is driven by `variant`, which selects the existing
 * checkbox class names so each surface stays visually identical — only the
 * collapsible chrome is shared.
 */
type ConsentVariant = "signup" | "auth-gate";

const VARIANT_CLASS: Record<ConsentVariant, { label: string; box: string; text: string }> = {
  signup: { label: "v-field-u__opt", box: "box", text: "text" },
  "auth-gate": {
    label: "auth-gate__check",
    box: "auth-gate__check-box",
    text: "auth-gate__check-txt",
  },
};

/**
 * The consent disclosure copy on its own: a condensed summary carrying all
 * the material TCPA terms, with the formal long-form paragraph behind an
 * animated "Read full SMS consent" toggle. Rendered both as a checkbox label
 * (inside <MarketingConsent>) and standalone beside a button-based opt-in
 * (the auth-gate nudge) — anywhere an affirmative consent action needs its
 * disclosure attached at the point of action. `textClassName` carries the
 * surface's text color.
 */
export function ConsentDisclosure({
  textClassName,
}: {
  textClassName: string;
}): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false);

  // Inside a <label> this stops the click from toggling the checkbox; it's
  // harmless when used standalone.
  const onToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  return (
    <span className={textClassName}>
      I expressly consent to receive marketing texts, calls &amp; voicemails from{" "}
      <strong>Sports Academy Games</strong> at the number provided, including via autodialer or
      prerecorded voice. Consent is not a condition of purchase. Msg &amp; data rates may apply.
      Reply STOP to opt out. View our <ConsentLink to="/terms">Terms of Service</ConsentLink> and{" "}
      <ConsentLink to="/privacy">Privacy Policy</ConsentLink>.{" "}
      <button
        type="button"
        className="tcpa-consent__toggle"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        {expanded ? "Hide full consent" : "Read full SMS consent"}
      </button>
      {/* Always mounted so the height can animate both ways; the grid-rows
       * trick collapses it to 0fr. aria-hidden keeps the collapsed legalese
       * out of the a11y tree — that's fine, every material term is already
       * visible in the summary above. */}
      <span className={`tcpa-consent__full${expanded ? " is-open" : ""}`} aria-hidden={!expanded}>
        <span className="tcpa-consent__full-inner">
          <TcpaConsentText />
        </span>
      </span>
    </span>
  );
}

/**
 * Terms/Privacy link with the consent-surface conventions baked in: opens in
 * a new tab (so reading it doesn't tear down a half-filled signup form) and
 * stops click propagation so following it never toggles the checkbox the
 * text labels.
 */
function ConsentLink({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Link to={to} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
      {children}
    </Link>
  );
}

interface MarketingConsentProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  variant: ConsentVariant;
}

export function MarketingConsent({
  checked,
  onChange,
  variant,
}: MarketingConsentProps): React.ReactElement {
  const cls = VARIANT_CLASS[variant];

  return (
    <label className={cls.label}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className={cls.box} aria-hidden="true">
        <ConsentCheckIcon />
      </span>
      <ConsentDisclosure textClassName={cls.text} />
    </label>
  );
}

function ConsentCheckIcon(): React.ReactElement {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
